"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  History,
  Heart,
  MapPin,
  Briefcase,
  Calendar,
  Shield,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface ListingItem {
  id: string;
  thumbnailUrl?: string;
  isLiked?: boolean;
  listing: {
    title: string;
    price: number;
  };
}

interface RightSidebarTabsProps {
  isExpanded: boolean;
  onToggle?: (expanded: boolean) => void;
  history: ListingItem[];
  liked: ListingItem[];
  isAdmin: boolean;
  onItemSelect: (id: string) => void;
  onClearHistory: () => void;
  onClearLiked: () => void;
  activeListingId?: string | null;
}

export function RightSidebarTabs({
  isExpanded,
  onToggle,
  history,
  liked,
  isAdmin,
  onItemSelect,
  onClearHistory,
  onClearLiked,
  activeListingId,
}: RightSidebarTabsProps) {
  // Local UI state for expanding accordion drawers
  const [expandedSection, setExpandedSection] = React.useState<string | null>(
    "history",
  );
  const [showAllHistoryModal, setShowAllHistoryModal] = React.useState(false);
  const [showAllFavoritesModal, setShowAllFavoritesModal] =
    React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);

  // Set mounted state to securely handle badge lengths on hydration
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const toggleSidebar = () => {
    if (onToggle) {
      onToggle(!isExpanded);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderListingItem = (item: ListingItem, isLiked: boolean = false) => {
    const thumbnailUrl = item.thumbnailUrl;
    const isActive = activeListingId === item.id;

    return (
      <div
        key={item.id}
        className={cn(
          "group relative flex cursor-pointer items-center gap-3 p-2 text-sm transition-all",
          isActive
            ? "border border-blue-400 bg-blue-500 font-medium text-white"
            : "hover:text-text-primary hover:shadow-theme-md text-slate-100 hover:bg-accent/50",
        )}
        onClick={() => onItemSelect(item.id)}
      >
        {/* Thumbnail Container */}
        <div className="rounded-theme-sm relative h-8 w-8 shrink-0 overflow-hidden bg-muted">
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              alt={item.listing.title}
              fill
              className="object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <MapPin className="text-icon-muted h-4 w-4" />
            </div>
          )}
          {isLiked && (
            <div className="bg-accent-red absolute bottom-0 right-0 rounded-tl px-0.5">
              <Heart className="h-2.5 w-2.5 fill-current text-white" />
            </div>
          )}
        </div>

        {/* Title and details */}
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">
            {item.listing.title}
          </div>
          <div className="text-white truncate text-xs">
            ${item.listing.price.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  const renderAllListingsModal = (
    items: ListingItem[],
    title: string,
    isLiked: boolean = false,
  ) => (
    <ScrollArea className="max-h-[500px]">
      <div className="space-y-2 p-4">
        {items.length === 0 ? (
          <div className="text-text-muted py-8 text-center text-sm">
            No items yet
          </div>
        ) : (
          items.map((item) => renderListingItem(item, isLiked))
        )}
      </div>
    </ScrollArea>
  );

  if (!isExpanded) {
    /* ----------------------------------------------------
       COLLAPSED VIEW
       ---------------------------------------------------- */
    return (
      <TooltipProvider delayDuration={0}>
        <div
          className={cn(
             "shadow0theme-xl fixed right-0 top-16 h-[calc(100vh-4rem)] border-l transition-all duration-300 ease-in-out",
            "z-sidebar w-16 bg-slate-500",
            "hidden flex-col overflow-hidden md:flex",
          )}
          style={{ boxShadow: "inset 1px 0 15px var(--theme-primary-glow)" }}
          data-theme-bg="true"
          data-state="collapsed"
        >
          {/* Collapse Panel Floating Toggle Button */}
          <button
            onClick={toggleSidebar}
            className={cn(
              "z-tooltip absolute -left-3 top-1/2 -translate-y-1/2",
              "h-8 w-8 rounded-full",
              "bg-gradient-to-br from-blue-500 to-blue-600",
              "border-border-hover border",
              "hover:shadow-theme-glow-lg shadow-theme-glow",
              "transition-all duration-300 ease-out",
              "hover:rotate-180 hover:scale-110",
              "group flex items-center justify-center",
            )}
            aria-label="Expand sidebar"
          >
            <ChevronLeft className="text-button-text h-4 w-4 transition-transform group-hover:scale-110" />
          </button>

          {/* Collapsed Icon Stack Container */}
          <div className="flex h-full flex-col items-center justify-between px-2 py-20">
            <div className="flex flex-col items-center gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      onToggle?.(true);
                      setExpandedSection("history");
                    }}
                    className="rounded-theme-lg group relative px-3 py-2 transition-colors hover:bg-blue-500/20"
                  >
                    <History
                      className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                      style={{
                        filter:
                          "drop-shadow(0 0 4px var(--theme-primary-glow))",
                      }}
                    />
                    {isMounted && history.length > 0 && (
                      <span className="shadow-theme-sm absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                        {history.length}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">History</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      onToggle?.(true);
                      setExpandedSection("favorites");
                    }}
                    className="rounded-theme-lg group relative px-3 py-2 transition-colors hover:bg-blue-500/20"
                  >
                    <Heart
                      className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                      style={{
                        filter:
                          "drop-shadow(0 0 4px var(--theme-primary-glow))",
                      }}
                    />
                    {isMounted && liked.length > 0 && (
                      <span className="shadow-theme-sm absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                        {liked.length}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left">Favorites</TooltipContent>
              </Tooltip>

              <Separator className="bg-border-muted mx-auto my-2 w-10" />

              {/* Individual Broker Tools */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href="/user/listings"
                    className="rounded-theme-lg hover:bg-primary-default/10 group block px-3 py-2 transition-colors"
                  >
                    <MapPin
                      className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                      style={{
                        filter:
                          "drop-shadow(0 0 4px var(--theme-primary-glow))",
                      }}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="left">Manage Listings</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href="/dashboard"
                    className="rounded-theme-lg hover:bg-primary-default/10 group block px-3 py-2 transition-colors"
                  >
                    <Briefcase
                      className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                      style={{
                        filter:
                          "drop-shadow(0 0 4px var(--theme-primary-glow))",
                      }}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="left">Dashboard</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href="/user/calendar"
                    className="rounded-theme-lg hover:bg-primary-default/10 group block px-3 py-2 transition-colors"
                  >
                    <Calendar
                      className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                      style={{
                        filter:
                          "drop-shadow(0 0 4px var(--theme-primary-glow))",
                      }}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="left">
                  Calendar & Scheduling
                </TooltipContent>
              </Tooltip>

              {isAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      href="/admin"
                      className="rounded-theme-lg hover:bg-primary-default/10 group block px-3 py-2 transition-colors"
                    >
                      <Shield
                        className="h-5 w-5 text-gray-300 transition-colors group-hover:text-blue-400"
                        style={{
                          filter:
                            "drop-shadow(0 0 4px var(--theme-primary-glow))",
                        }}
                      />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="left">Admin</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  /* ----------------------------------------------------
     EXPANDED VIEW
     ---------------------------------------------------- */
  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "border-border-default shadow-theme-xl fixed right-0 top-16 z-[var(--z-sidebar)] h-[calc(100vh-4rem)] border-l transition-all duration-300 ease-in-out",
          isExpanded
            ? "z-[var(--z-sidebar)] w-64"
            : "z-[var(--z-sidebar)] w-16",
          "hidden overflow-hidden bg-slate-600 md:block",
        )}
        style={{ boxShadow: "inset 1px 0 20px var(--theme-primary-glow)" }}
        data-theme-bg="true"
        data-state="expanded"
      >
        {/* Expanded Panel Floating Toggle Button */}
        <button
          onClick={toggleSidebar}
          className={cn(
            "z-tooltip absolute -left-3 top-1/2 -translate-y-1/2",
            "h-8 w-8 rounded-full",
            "bg-gradient-to-br from-blue-500 to-blue-600",
            "border-border-hover border",
            "hover:shadow-theme-glow-lg shadow-theme-glow",
            "transition-all duration-300 ease-out",
            "hover:rotate-180 hover:scale-110",
            "group flex items-center justify-center",
          )}
          style={{ boxShadow: "0 0 20px rgba217, 165, 20 0.5)" }}
          aria-label="Collapse sidebar"
        >
          <ChevronRight className="text-button-text h-4 w-4 transition-transform group-hover:scale-110" />
        </button>

        {/* Content Container */}
        <div className="flex h-full flex-col pt-16">
          {/* History Section */}
          <div className="border-border-muted/40 border-b">
            <button
              onClick={() => toggleSection("history")}
              className="hover:bg-primary-default/5 group flex w-full items-center justify-between px-4 py-3 transition-colors"
            >
              <div className="flex items-center gap-3">
                <History
                  className={cn(
                    "h-5 w-5 transition-colors",
                    expandedSection === "history"
                      ? "text-text-white"
                      : "text-icon-muted group-hover:text-icon-accent",
                  )}
                  style={
                    expandedSection === "history"
                      ? {
                          filter:
                            "drop-shadow(0 0 6px var(--theme-primary-glow))",
                        }
                      : undefined
                  }
                />
                <span
                  className={cn(
                    "text-sm transition-colors",
                    expandedSection === "history"
                      ? "text-text-primary font-semibold"
                      : "group-hover: font-medium",
                  )}
                >
                  History
                </span>
                <span className="text-text-muted text-xs">
                  ({history.length})
                </span>
              </div>
              {expandedSection === "history" ? (
                <ChevronUp className="text-text-secondary h-4 w-4" />
              ) : (
                <ChevronDown className="text-text-muted group-hover:text-text-secondary h-4 w-4" />
              )}
            </button>

            {expandedSection === "history" && (
              <div className="px-2 pb-3 pt-1">
                {history.length === 0 ? (
                  <div className="text-text-muted py-8 text-center text-xs">
                    No viewing history yet
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      {history
                        .slice(0, 5)
                        .map((item) => renderListingItem(item, false))}
                    </div>
                    {history.length > 5 && (
                      <Dialog
                        open={showAllHistoryModal}
                        onOpenChange={setShowAllHistoryModal}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            className="text-text-secondary hover:text-text-primary mt-2 w-full text-xs hover:bg-accent/50"
                          >
                            View All ({history.length})
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-modal-background border-border-default shadow-theme-xl max-w-2xl border">
                          <DialogHeader>
                            <DialogTitle className="text-text-primary">
                              Viewing History
                            </DialogTitle>
                            <DialogDescription className="text-text-muted">
                              All properties you've viewed recently
                            </DialogDescription>
                          </DialogHeader>
                          {renderAllListingsModal(history, "History", false)}
                          <div className="border-border-muted/30 flex items-center justify-between border-t pt-4">
                            <Button
                              variant="ghost"
                              onClick={onClearHistory}
                              size="sm"
                              className="text-text-error hover:bg-accent-red/10 hover:text-text-error"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Clear All
                            </Button>
                            <Button
                              onClick={() => setShowAllHistoryModal(false)}
                              className="bg-button-background text-button-text hover:bg-button-hover"
                            >
                              Close
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {history.length > 0 && history.length <= 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearHistory}
                        className="text-text-muted hover:text-text-error hover:bg-accent-red/5 mt-2 w-full text-xs"
                      >
                        <Trash2 className="mr-2 h-3 w-3" />
                        Clear History
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Favorites Section */}
          <div className="border-border-muted/40 border-b">
            <button
              onClick={() => toggleSection("favorites")}
              className="hover:bg-primary-default/5 group flex w-full items-center justify-between px-4 py-3 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Heart
                  className={cn(
                    "h-5 w-5 transition-colors",
                    expandedSection === "favorites"
                      ? "text-text-accent"
                      : "text-icon-muted group-hover:text-icon-accent",
                  )}
                  style={
                    expandedSection === "favorites"
                      ? {
                          filter:
                            "drop-shadow(0 0 6px var(--theme-primary-glow))",
                        }
                      : undefined
                  }
                />
                <span
                  className={cn(
                    "text-sm transition-colors",
                    expandedSection === "favorites"
                      ? "text-text-primary font-semibold"
                      : "text-text-secondary group-hover:text-text-primary font-medium",
                  )}
                >
                  Favorites
                </span>
                <span className="text-text-muted text-xs">
                  ({liked.length})
                </span>
              </div>
              {expandedSection === "favorites" ? (
                <ChevronUp className="text-text-secondary h-4 w-4" />
              ) : (
                <ChevronDown className="text-text-muted group-hover:text-text-secondary h-4 w-4" />
              )}
            </button>

            {expandedSection === "favorites" && (
              <div className="px-2 pb-3 pt-1">
                {liked.length === 0 ? (
                  <div className="text-text-muted py-8 text-center text-xs">
                    No favorites yet
                  </div>
                ) : (
                  <>
                    <div className="space-y-1">
                      {liked
                        .slice(0, 5)
                        .map((item) => renderListingItem(item, true))}
                    </div>
                    {liked.length > 5 && (
                      <Dialog
                        open={showAllFavoritesModal}
                        onOpenChange={setShowAllFavoritesModal}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            className="text-text-secondary hover:text-text-primary mt-2 w-full text-xs hover:bg-accent/50"
                          >
                            View All ({liked.length})
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-modal-background border-border-default shadow-theme-xl max-w-2xl border">
                          <DialogHeader>
                            <DialogTitle className="text-text-primary">
                              Favorite Properties
                            </DialogTitle>
                            <DialogDescription className="text-text-muted">
                              All properties you've saved as favorites
                            </DialogDescription>
                          </DialogHeader>
                          {renderAllListingsModal(liked, "Favorites", true)}
                          <div className="border-border-muted/30 flex items-center justify-between border-t pt-4">
                            <Button
                              variant="ghost"
                              onClick={onClearLiked}
                              size="sm"
                              className="text-text-error hover:bg-accent-red/10 hover:text-text-error"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Clear All
                            </Button>
                            <Button
                              onClick={() => setShowAllFavoritesModal(false)}
                              className="bg-button-background text-button-text hover:bg-button-hover"
                            >
                              Close
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {liked.length > 0 && liked.length <= 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearLiked}
                        className="text-text-muted hover:text-text-error hover:bg-accent-red/5 mt-2 w-full text-xs"
                      >
                        <Trash2 className="mr-2 h-3 w-3" />
                        Clear Favorites
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Tools for Brokers Section */}
          <div className="border-border-muted/40 border-b">
            <button
              onClick={() => toggleSection("tools")}
              className="hover:bg-primary-default/5 group flex w-full items-center justify-between px-4 py-3 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Briefcase
                  className={cn(
                    "h-5 w-5 transition-colors",
                    expandedSection === "tools"
                      ? "text-text-accent"
                      : "text-icon-muted group-hover:text-icon-accent",
                  )}
                  style={
                    expandedSection === "tools"
                      ? {
                          filter:
                            "drop-shadow(0 0 6px var(--theme-primary-glow))",
                        }
                      : undefined
                  }
                />
                <span
                  className={cn(
                    "text-sm transition-colors",
                    expandedSection === "tools"
                      ? "text-text-primary font-semibold"
                      : "text-text-secondary group-hover:text-text-primary font-medium",
                  )}
                >
                  Tools for Brokers
                </span>
              </div>
              {expandedSection === "tools" ? (
                <ChevronUp className="text-text-secondary h-4 w-4" />
              ) : (
                <ChevronDown className="text-text-muted group-hover:text-text-secondary h-4 w-4" />
              )}
            </button>

            {expandedSection === "tools" && (
              <div className="space-y-1 px-2 pb-3 pt-2">
                <Link
                  href="/user/listings"
                  className="rounded-theme-md text-text-secondary hover:text-text-primary flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-accent/50"
                >
                  <MapPin className="text-icon-muted h-4 w-4" />
                  <span>Manage Listings</span>
                </Link>
                <Link
                  href="/dashboard"
                  className="rounded-theme-md text-text-secondary hover:text-text-primary flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-accent/50"
                >
                  <Briefcase className="text-icon-muted h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  href="/user/calendar"
                  className="rounded-theme-md text-text-secondary hover:text-text-primary flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-accent/50"
                >
                  <Calendar className="text-icon-muted h-4 w-4" />
                  <span>Calendar & Scheduling</span>
                </Link>
              </div>
            )}
          </div>

          {/* Admin Section */}
          {isAdmin && (
            <div className="border-border-muted/40 border-b">
              <Link
                href="/admin"
                className="hover:bg-primary-default/5 group flex w-full items-center gap-3 px-4 py-3 transition-colors"
              >
                <Shield className="text-icon-muted group-hover:text-icon-accent h-5 w-5 transition-colors" />
                <span className="text-text-secondary group-hover:text-text-primary text-sm font-medium">
                  Admin Panel
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
